import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LoginService } from '../../login/login.service';
import { Router } from '@angular/router';
import { Permissions } from '../../auth-guard.guard';
import { CommonApiService } from '../../common-api.service';
import { SnackbarService } from '../../snackbar.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../environments/environment';

interface QuickAction {
  route: string;
  icon: string;
  title: string;
  description: string;
}

interface ReworkRow {
  rework_id: number;
  job_id: number;
  job_no: string;
  patient_name: string;
  doctor_name: string;
  product_name: string;
  rework_process: string;
  from_process: string;
  department_name: string;
  employee_name: string;
  rework_reason: string;
  created_at: string;
}

interface HomeSummaryRow {
  job_id: number;
  job_no: string;
  patient_name: string;
  doctor_name: string;
  product_name: string;
  job_date: string;
  delivery_date: string;
  job_status: string;
  current_status: string;
  type_label: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('reworkPaginator') reworkPaginator!: MatPaginator;
  @ViewChild('summaryPaginator') summaryPaginator!: MatPaginator;

  currentUser: any;
  userName = '';
  today = new Date();

  // Rework queue follows role-based visibility from the backend.
  isAdmin = false;
  summaryLoading = false;
  reworkLoading = false;
  summaryExporting = false;
  summaryRows: HomeSummaryRow[] = [];
  summaryTotal = 0;
  summaryPageIndex = 0;
  summaryPageSize = 10;
  summarySearchTerm = '';
  summarySortField = 'delivery_date';
  summarySortDirection: 'asc' | 'desc' = 'asc';
  summaryColumns = [
    'sr_no',
    'doctor_name',
    'patient_name',
    'job_no',
    'product_name',
    'job_date',
    'delivery_date',
    'job_status',
    'current_status',
    'type_label',
  ];
  reworkRows: ReworkRow[] = [];
  reworkTotal = 0;
  reworkPageIndex = 0;
  reworkPageSize = 10;
  reworkColumns = [
    'job_no',
    'doctor_name',
    'product_name',
    'rework_process',
    'department_name',
    'rework_reason',
    'created_at',
  ];

  quickActions: QuickAction[] = [
    { route: '/joborder', icon: 'assignment', title: 'Job Entry', description: 'Create and manage job orders' },
    { route: '/jobschedule', icon: 'precision_manufacturing', title: 'Production', description: 'Track production status' },
    { route: '/challan', icon: 'local_shipping', title: 'Challan', description: 'Manage delivery challans' },
    { route: '/reports', icon: 'insert_chart', title: 'Reports', description: 'View business reports' },
    { route: '/doctor', icon: 'person', title: 'Doctors', description: 'Manage doctor records' },
    { route: '/accounting', icon: 'account_balance', title: 'Accounting', description: 'Financial management' },
  ];
  private summarySearchDebounceHandle: ReturnType<typeof window.setTimeout> | null = null;
  private summaryRequestSequence = 0;

  constructor(
    private auth: LoginService,
    private router: Router,
    public permissions: Permissions,
    private api: CommonApiService,
    private snackbar: SnackbarService,
    private http: HttpClient,
    private cookie: CookieService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.auth.currentUserValue;
    this.userName = this.currentUser?.user?.username || 'User';
    this.isAdmin = this.currentUser?.user?.role?.name === 'Admin';
    this.loadHomeSummary();
    this.loadReworkQueue();
  }

  ngOnDestroy(): void {
    if (this.summarySearchDebounceHandle !== null) {
      window.clearTimeout(this.summarySearchDebounceHandle);
    }
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }

  get visibleQuickActions(): QuickAction[] {
    return this.quickActions.filter((action) =>
      this.permissions.canActivate(this.currentUser?.user, action.route),
    );
  }

  loadHomeSummary(): void {
    this.summaryLoading = true;
    const requestSequence = ++this.summaryRequestSequence;
    const payload = {
      pageIndex: this.summaryPageIndex,
      pageSize: this.summaryPageSize,
      searchTerm: this.summarySearchTerm.trim(),
      sortField: this.summarySortField,
      sortDirection: this.summarySortDirection,
    };
    this.api.callRoute('joborder', '/home_summary/list', payload).subscribe({
      next: (res: any) => {
        if (requestSequence !== this.summaryRequestSequence) {
          return;
        }
        this.summaryRows = res?.items || [];
        this.summaryTotal = res?.rowCount || 0;
        this.summaryLoading = false;
      },
      error: (err: any) => {
        if (requestSequence !== this.summaryRequestSequence) {
          return;
        }
        this.summaryLoading = false;
        this.snackbar.error(err?.error?.message || 'Failed to load home page summary');
      },
    });
  }

  loadReworkQueue(): void {
    this.reworkLoading = true;
    const payload = { pageIndex: this.reworkPageIndex, pageSize: this.reworkPageSize };
    this.api.callRoute('joborder', '/list/rework-queue', payload).subscribe({
      next: (res: any) => {
        this.reworkRows = res?.items || [];
        this.reworkTotal = res?.rowCount || 0;
        this.reworkLoading = false;
      },
      error: (err: any) => {
        this.reworkLoading = false;
        this.snackbar.error(err?.error?.message || 'Failed to load rework queue');
      },
    });
  }

  onReworkPage(event: PageEvent): void {
    this.reworkPageIndex = event.pageIndex;
    this.reworkPageSize = event.pageSize;
    this.loadReworkQueue();
  }

  onSummaryPage(event: PageEvent): void {
    this.summaryPageIndex = event.pageIndex;
    this.summaryPageSize = event.pageSize;
    this.loadHomeSummary();
  }

  onSummarySortChange(sort: Sort): void {
    this.summarySortField = sort.active || 'delivery_date';
    this.summarySortDirection = sort.direction === 'desc' ? 'desc' : 'asc';

    if (this.resetSummaryToFirstPage()) {
      return;
    }

    this.loadHomeSummary();
  }

  onSummarySearchTermChange(value: string): void {
    this.summarySearchTerm = value;
    if (this.summarySearchDebounceHandle !== null) {
      window.clearTimeout(this.summarySearchDebounceHandle);
    }

    this.summarySearchDebounceHandle = window.setTimeout(() => {
      if (this.resetSummaryToFirstPage()) {
        return;
      }

      this.loadHomeSummary();
    }, 250);
  }

  refreshHomeSummary(): void {
    if (this.resetSummaryToFirstPage()) {
      return;
    }

    this.loadHomeSummary();
  }

  refreshReworkQueue(): void {
    this.reworkPageIndex = 0;
    if (this.reworkPaginator) {
      this.reworkPaginator.firstPage();
    }
    this.loadReworkQueue();
  }

  summarySerialNumber(index: number): number {
    return this.summaryPageIndex * this.summaryPageSize + index + 1;
  }

  openSummaryJob(row: HomeSummaryRow): void {
    if (row?.job_id) {
      this.router.navigate(['/joborder', row.job_id]);
    }
  }

  openReworkJob(row: ReworkRow): void {
    if (row?.job_id) {
      this.router.navigate(['/joborder', row.job_id]);
    }
  }

  exportHomeSummary(): void {
    if (this.summaryExporting) {
      return;
    }
    this.summaryExporting = true;
    const headers = new HttpHeaders({ Authorization: this.cookie.get('session-key') });
    const payload = {
      searchTerm: this.summarySearchTerm.trim(),
      sortField: this.summarySortField,
      sortDirection: this.summarySortDirection,
    };
    this.http
      .post(`${environment.api}joborder/home_summary/export`, payload, { headers, responseType: 'blob' })
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Home_Page_Summary.xlsx';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.summaryExporting = false;
        },
        error: (err: any) => {
          this.summaryExporting = false;
          this.snackbar.error(err?.error?.message || 'Failed to export home page summary');
        },
      });
  }

  private resetSummaryToFirstPage(): boolean {
    if (this.summaryPageIndex === 0) {
      return false;
    }

    this.summaryPageIndex = 0;
    if (this.summaryPaginator) {
      this.summaryPaginator.firstPage();
      return true;
    }

    return false;
  }
}
